import { SxProps, Theme } from "@mui/material/styles";
import { CatalogLicenseWindow } from "./availsPlusDetailTypes";

/** Neutral charcoal surface — avoids the blue tint of theme `background.paper`. */
export const AVAILS_DETAIL_DRAWER_BG = "#1A2027";

export const AVAILS_DETAIL_DRAWER_COLORS = {
    bg: AVAILS_DETAIL_DRAWER_BG,
    textPrimary: "#f5f5f5",
    textSecondary: "#d1d5db",
    border: "rgba(255, 255, 255, 0.14)",
    rowMuted: "rgba(255, 255, 255, 0.04)",
    rowCurrent: "rgba(76, 175, 80, 0.24)",
} as const;

export const detailDrawerRootSx: SxProps<Theme> = {
    bgcolor: AVAILS_DETAIL_DRAWER_COLORS.bg,
    color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
};

export const detailDrawerPaperSx: SxProps<Theme> = {
    bgcolor: "transparent",
    color: "inherit",
    borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
    backgroundImage: "none",
    boxShadow: "none",
};

export const detailDrawerTableSx: SxProps<Theme> = {
    ...detailDrawerPaperSx,
    "& .MuiTableCell-root": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
        borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
    },
    "& .MuiTableCell-head": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
        fontWeight: 600,
    },
    "& .MuiTableCell-root:focus, & .MuiTableCell-root:focus-within": {
        outline: "none",
    },
};

export const detailDrawerFormControlSx: SxProps<Theme> = {
    "& .MuiInputLabel-root": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
    },
    "& .MuiOutlinedInput-root": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
        "& fieldset": {
            borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255, 255, 255, 0.28)",
        },
        "&.Mui-focused fieldset": {
            borderColor: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
        },
    },
    "& .MuiSelect-icon": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
    },
    "& .Mui-disabled": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
        WebkitTextFillColor: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
    },
};

export const detailDrawerTabsSx: SxProps<Theme> = {
    bgcolor: AVAILS_DETAIL_DRAWER_COLORS.bg,
    borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
    minHeight: 44,
    "& .MuiTab-root": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
        minHeight: 44,
    },
    "& .MuiTab-root.Mui-selected": {
        color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
    },
    "& .MuiTab-root.Mui-disabled": {
        color: "rgba(255, 255, 255, 0.35)",
    },
    "& .MuiTabs-indicator": {
        backgroundColor: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
    },
};

export const detailDrawerVerticalTabsSx: SxProps<Theme> = {
    ...detailDrawerTabsSx,
    minHeight: 0,
    "& .MuiTabs-flexContainer": {
        alignItems: "stretch",
        gap: 0.25,
    },
    "& .MuiTab-root": {
        alignItems: "flex-start",
        justifyContent: "flex-start",
        textAlign: "left",
        minHeight: 36,
        py: 0.75,
        px: 2,
        fontSize: "0.8125rem",
        fontWeight: 500,
        letterSpacing: "0.04em",
        borderRadius: 1,
        transition: "background-color 0.15s ease",
    },
    "& .MuiTab-root.Mui-selected": {
        bgcolor: "rgba(255, 255, 255, 0.06)",
    },
    "& .MuiTabs-indicator": {
        left: 0,
        right: "auto",
        width: 3,
        borderRadius: 1,
    },
};

export function licenseWindowRowSx(status: CatalogLicenseWindow["status"]): SxProps<Theme> {
    if (status === "past") {
        return {
            bgcolor: AVAILS_DETAIL_DRAWER_COLORS.rowMuted,
            "& .MuiTableCell-root": {
                color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
            },
        };
    }
    if (status === "current") {
        return {
            bgcolor: AVAILS_DETAIL_DRAWER_COLORS.rowCurrent,
            "& .MuiTableCell-root": {
                color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
                fontWeight: 600,
            },
        };
    }
    return {
        "& .MuiTableCell-root": {
            color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
        },
    };
}

export const detailDrawerLabelSx: SxProps<Theme> = {
    color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
};

export const detailDrawerValueSx: SxProps<Theme> = {
    color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
};

export const detailDrawerSectionTitleSx: SxProps<Theme> = {
    color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
    fontWeight: 600,
};

export const detailDrawerHierarchyItemSx: SxProps<Theme> = {
    color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
    py: 0.35,
    px: 1,
    borderRadius: 1,
    minHeight: 0,
    "&:hover": {
        bgcolor: "rgba(255, 255, 255, 0.06)",
    },
};

export const detailDrawerHierarchyLinkSx: SxProps<Theme> = {
    color: "#90caf9",
    cursor: "pointer",
    "&:hover": {
        textDecoration: "underline",
    },
};

export const detailDrawerHierarchyMetaSx: SxProps<Theme> = {
    color: AVAILS_DETAIL_DRAWER_COLORS.textSecondary,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "0.8rem",
};

export const detailDrawerHierarchyCurrentSx: SxProps<Theme> = {
    color: AVAILS_DETAIL_DRAWER_COLORS.textPrimary,
    fontWeight: 600,
};
